import { useEffect, useState } from 'react';
import unicornLogo from '../../assets/unicorn-logo.png';

// press-brass — the same gold the rest of the site runs on (tailwind.config.js).
const BRASS = { r: 199, g: 162, b: 82 };

// Vertical color ramp standing in for "white" — a brushed-chrome reflection
// pattern (soft shadow, bright highlight, soft shadow, highlight again)
// instead of a flat fill, so the body reads as polished metal rather than
// paint. Stops are spaced wide on purpose — narrow, high-contrast transitions
// read as a hard seam/line cutting across the shape rather than a soft
// reflection, so every band here spans at least ~0.2 of the height.
const CHROME_BAND_STOPS = [
  { t: 0.0, c: [152, 162, 172] },
  { t: 0.22, c: [252, 253, 254] },
  { t: 0.46, c: [176, 186, 196] },
  { t: 0.7, c: [255, 255, 255] },
  { t: 0.88, c: [180, 188, 196] },
  { t: 1.0, c: [230, 233, 236] },
];

function chromeBand(t) {
  const stops = CHROME_BAND_STOPS;
  let a = stops[0];
  let b = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i].t && t <= stops[i + 1].t) {
      a = stops[i];
      b = stops[i + 1];
      break;
    }
  }
  const span = b.t - a.t || 1;
  const f = (t - a.t) / span;
  return [
    a.c[0] + (b.c[0] - a.c[0]) * f,
    a.c[1] + (b.c[1] - a.c[1]) * f,
    a.c[2] + (b.c[2] - a.c[2]) * f,
  ];
}

// The source art is a black silhouette with a silver-gradient ribbon running
// through it, which reads as a dark hole on the ink hero. Rather than ship a
// second binary, recolor it in place with a gradient map: the black body
// (cap, whole head, mane/tail) maps to the chrome band above, the ribbon's
// silver highlight maps to brass, and every tone between lands on the ramp
// joining them — so the ribbon still dissolves into the body at its tips
// exactly as the original does, just brushed chrome with a gold stripe.
//
// Shared between the Home hero (animated star-field version) and any other
// page that wants the same gold/brass finish on the logo mark without the
// star animation — both must recolor the exact same source pixels so the
// mark reads identically wherever it appears.
export function useBrassLogo() {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    const img = new Image();
    img.src = unicornLogo;
    img.onload = () => {
      const idle = window.requestIdleCallback
        ? (cb) => window.requestIdleCallback(cb, { timeout: 200 })
        : (cb) => setTimeout(cb, 0);
      idle(() => {
        if (cancelled) return;
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const g = c.getContext('2d');
        g.drawImage(img, 0, 0);
        const frame = g.getImageData(0, 0, c.width, c.height);
        const d = frame.data;
        const rowBands = new Array(c.height);
        for (let y = 0; y < c.height; y++) rowBands[y] = chromeBand(y / c.height);
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] === 0) continue; // leave the transparent field alone
          const l = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;
          const y = Math.floor(i / 4 / c.width);
          const [cr, cg, cb] = rowBands[y];
          d[i] = Math.round(cr + (BRASS.r - cr) * l);
          d[i + 1] = Math.round(cg + (BRASS.g - cg) * l);
          d[i + 2] = Math.round(cb + (BRASS.b - cb) * l);
        }
        g.putImageData(frame, 0, 0);
        c.toBlob((blob) => {
          if (cancelled || !blob) return;
          url = URL.createObjectURL(blob);
          setSrc(url);
        }, 'image/png');
      });
    };
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);
  return src;
}

export default useBrassLogo;
